import React, { useState, useMemo } from 'react';
import { Contact, ContactFilters } from '../../../config/api';
import { highlightText } from '../../../utils/textHighlight';
import ContactDeletionErrorModal from '../../../components/ContactDeletionErrorModal';

interface ContactsTableViewProps {
  contacts: Contact[];
  loading: boolean;
  error: string | null;
  searchQuery: string;
  filters: ContactFilters;
  onContactClick: (contact: Contact) => void;
  onDeleteContacts?: (contactIds: string[]) => Promise<void>;
}

// Interface for matter information extracted from error messages
interface MatterInfo {
  matter_id: string;
  title?: string;
  description?: string;
  matter_number?: string;
}

// Interface for deletion error information
interface DeletionError {
  contactId: string;
  contactName: string;
  matters: MatterInfo[];
  error: string;
}

const ContactsTableView: React.FC<ContactsTableViewProps> = ({
  contacts,
  loading,
  error,
  searchQuery,
  filters,
  onContactClick,
  onDeleteContacts
}) => {
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<keyof Contact>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [deletionErrors, setDeletionErrors] = useState<DeletionError[]>([]);

  // Filter and sort contacts
  const filteredAndSortedContacts = useMemo(() => {
    const filtered = contacts.filter(contact => {
      // Search filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch = 
          contact.name.toLowerCase().includes(searchLower) ||
          contact.email?.toLowerCase().includes(searchLower) ||
          contact.phone?.toLowerCase().includes(searchLower);
        
        if (!matchesSearch) return false;
      }

      // Status filter
      if (filters.status && contact.status !== filters.status) {
        return false;
      }

      // Type filter
      if (filters.contact_type && contact.contact_type !== filters.contact_type) {
        return false;
      }

      return true;
    });

    // Sort contacts
    filtered.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      // Handle undefined values
      if (aValue === undefined && bValue === undefined) return 0;
      if (aValue === undefined) return sortDirection === 'asc' ? 1 : -1;
      if (bValue === undefined) return sortDirection === 'asc' ? -1 : 1;
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [contacts, searchQuery, filters, sortField, sortDirection]);

  const handleSort = (field: keyof Contact) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSelectAll = () => {
    if (selectedContacts.size === filteredAndSortedContacts.length) {
      setSelectedContacts(new Set());
    } else {
      setSelectedContacts(new Set(filteredAndSortedContacts.map(c => c.id)));
    }
  };

  const handleSelectContact = (contactId: string) => {
    const newSelected = new Set(selectedContacts);
    if (newSelected.has(contactId)) {
      newSelected.delete(contactId);
    } else {
      newSelected.add(contactId);
    }
    setSelectedContacts(newSelected);
  };

  // Enhanced function to parse error messages and extract matter information
  const parseErrorForMatters = (errorMessage: string, contactId: string): MatterInfo[] => {
    const matters: MatterInfo[] = [];
    
    console.log('🔍 [parseErrorForMatters] Parsing error for contact:', contactId);
    console.log('🔍 [parseErrorForMatters] Raw error message:', errorMessage);
    
    // First, try to parse structured error response from backend
    try {
      const errorData = JSON.parse(errorMessage);
      
      if (errorData.error_type === 'CLIENT_DELETION_BLOCKED' && errorData.associated_matters) {
        console.log('✅ [parseErrorForMatters] Found structured error data with matters:', errorData.associated_matters);
        console.log('✅ [parseErrorForMatters] This is a REAL matter-related deletion block');
        
        // Convert backend matter format to frontend MatterInfo format
        const structuredMatters = errorData.associated_matters.map((matter: any) => ({
          matter_id: matter.matter_id,
          title: matter.title || matter.description || 'Untitled Matter',
          description: matter.description || `${matter.stage || 'Active'} matter in ${matter.practice_area || 'General Practice'}`,
          matter_number: matter.matter_number
        }));
        
        matters.push(...structuredMatters);
        console.log('✅ [parseErrorForMatters] Successfully parsed REAL matters:', matters);
        return matters;
      } else if (errorData.error_type === 'CLIENT_DELETION_BLOCKED') {
        console.log('⚠️ [parseErrorForMatters] Structured error but NO associated_matters - this should not happen!');
        console.log('⚠️ [parseErrorForMatters] Full error data:', errorData);
      }
    } catch (jsonError) {
      console.log('🔍 [parseErrorForMatters] Not a structured JSON error, checking if this should show modal...');
    }
    
    // IMPORTANT: Only show the modal for constraint errors that are likely matter-related
    // If this is a different type of error, we should NOT show the modal
    
    // Check if this looks like a matter-related constraint error
    const matterRelatedKeywords = [
      'matter', 'dms_matters', 'foreign key', 'constraint', 
      'associated matter', 'active matter', 'delete or reassign'
    ];
    
    const hasMattersKeyword = matterRelatedKeywords.some(keyword => 
      errorMessage.toLowerCase().includes(keyword.toLowerCase())
    );
    
    if (!hasMattersKeyword) {
      console.log('❌ [parseErrorForMatters] Error does not appear to be matter-related, NOT showing modal');
      console.log('❌ [parseErrorForMatters] Error was:', errorMessage);
      return []; // Return empty array - no modal should show
    }
    
    console.log('🔍 [parseErrorForMatters] Error appears matter-related, proceeding with fallback parsing...');
    
    // Enhanced pattern matching for different error scenarios (ONLY for matter-related errors)
    
    // Pattern 1: Foreign key constraint violations (most common)
    if (errorMessage.includes('foreign key constraint') || 
        errorMessage.includes('FOREIGN KEY constraint') ||
        errorMessage.includes('violates foreign key') ||
        errorMessage.includes('constraint') ||
        errorMessage.includes('Failed to delete contact')) {
      
      console.log('✅ [parseErrorForMatters] Found matter-related constraint violation pattern');
      
      // Look for specific client/matter references in the error
      const clientIdMatches = errorMessage.match(/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/g);
      
      if (clientIdMatches && clientIdMatches.length > 0) {
        console.log('✅ [parseErrorForMatters] Found client IDs:', clientIdMatches);
        
        // Create a matter entry for the constraint violation
        matters.push({
          matter_id: 'constraint-violation',
          title: 'Database Relationship Constraint',
          description: 'This contact cannot be deleted because it has active relationships that reference it (likely matters or billing records).',
          matter_number: 'CONSTRAINT'
        });
      } else {
        // Generic constraint violation
        matters.push({
          matter_id: 'constraint-violation',
          title: 'Data Relationship Constraint',
          description: 'This contact cannot be deleted due to existing data relationships.',
          matter_number: 'CONSTRAINT'
        });
      }
    }
    
    // Pattern 2: Look for specific matter/client ID references
    const matterIdRegex = /(?:matter[_\s]?id|client[_\s]?id)[:\s]*([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/gi;
    const matterIdMatches = errorMessage.matchAll(matterIdRegex);
    
    for (const match of matterIdMatches) {
      console.log('✅ [parseErrorForMatters] Found ID reference:', match[1]);
      matters.push({
        matter_id: match[1],
        title: 'Referenced Record',
        description: 'This record is preventing contact deletion',
        matter_number: match[1].slice(0, 8).toUpperCase()
      });
    }
    
    // Pattern 3: Look for table references (dms_matters, etc.)
    if (errorMessage.includes('dms_matters') || 
        errorMessage.includes('matters table') ||
        errorMessage.includes('matter')) {
      
      console.log('✅ [parseErrorForMatters] Found matter table reference');
      
      if (matters.length === 0) {
        matters.push({
          matter_id: 'matter-table-ref',
          title: 'Matter Table Reference',
          description: 'Database constraint prevents deletion due to matter table references.',
          matter_number: 'MATTER-REF'
        });
      }
    }
    
    // Pattern 4: Fallback - if we have any matter-related error, assume it's matter-related
    if (matters.length === 0 && hasMattersKeyword) {
      console.log('✅ [parseErrorForMatters] Using fallback pattern for matter-related error');
      matters.push({
        matter_id: 'deletion-error',
        title: 'Matter-Related Deletion Error',
        description: 'This contact cannot be deleted due to associated matters or related data.',
        matter_number: 'ERROR'
      });
    }
    
    console.log('🎯 [parseErrorForMatters] Final matters array:', matters);
    console.log('🎯 [parseErrorForMatters] Will show modal:', matters.length > 0);
    return matters;
  };

  // Handle bulk delete with enhanced error parsing
  const handleBulkDelete = async () => {
    if (!onDeleteContacts || selectedContacts.size === 0) return;

    const selectedContactsArray = Array.from(selectedContacts);

    try {
      setIsDeleting(true);
      await onDeleteContacts(selectedContactsArray);
      
      // Clear selection after successful deletion
      setSelectedContacts(new Set());
      console.log(`✅ Successfully deleted ${selectedContactsArray.length} contacts`);
      
    } catch (error) {
      console.error('❌ Failed to delete contacts:', error);
      
      // Parse the error to create structured deletion errors
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.log('🔍 [handleBulkDelete] Processing error message:', errorMessage);
      
      const errors: DeletionError[] = [];
      
      // Try to extract individual contact errors from the bulk error message
      const lines = errorMessage.split('\n');
      console.log('🔍 [handleBulkDelete] Error message lines:', lines);
      
      for (const contactId of selectedContactsArray) {
        const contactName = contacts.find(c => c.id === contactId)?.name || 'Unknown Contact';
        
        console.log(`🔍 [handleBulkDelete] Processing contact ${contactId} (${contactName})`);
        
        // Look for lines that mention this contact ID, or use the whole error if it's a single contact
        let contactErrorMessage = errorMessage;
        
        if (selectedContactsArray.length > 1) {
          const contactErrorLines = lines.filter(line => line.includes(contactId));
          if (contactErrorLines.length > 0) {
            contactErrorMessage = contactErrorLines.join(' ');
          }
        }
        
        console.log(`🔍 [handleBulkDelete] Contact error message for ${contactId}:`, contactErrorMessage);
        
        // Parse matter information from the error
        const matters = parseErrorForMatters(contactErrorMessage, contactId);
        
        console.log(`🔍 [handleBulkDelete] Parsed matters for ${contactId}:`, matters);
        
        // Add to errors if we found any matters (which should be always with the improved parsing)
        if (matters.length > 0) {
          errors.push({
            contactId,
            contactName,
            matters,
            error: contactErrorMessage
          });
        }
      }
      
      console.log('🎯 [handleBulkDelete] Final errors array:', errors);
      
      // Always show the beautiful modal for any deletion failure
      if (errors.length > 0) {
        console.log('✅ [handleBulkDelete] Showing beautiful error modal');
        setDeletionErrors(errors);
        setShowErrorModal(true);
      } else {
        // Fallback: if parsing failed completely, create a generic error for the modal
        console.log('⚠️ [handleBulkDelete] Parsing failed, creating fallback error');
        const fallbackErrors: DeletionError[] = selectedContactsArray.map(contactId => {
          const contactName = contacts.find(c => c.id === contactId)?.name || 'Unknown Contact';
          return {
            contactId,
            contactName,
            matters: [{
              matter_id: 'parse-failure',
              title: 'Deletion Failed',
              description: 'Contact deletion failed. This may be due to associated matters or other data relationships.',
              matter_number: 'FAILED'
            }],
            error: errorMessage
          };
        });
        
        setDeletionErrors(fallbackErrors);
        setShowErrorModal(true);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'converted': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'qualified': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'lead': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'archived': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading contacts...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
        <p className="text-red-800 dark:text-red-200">{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Bulk actions bar */}
      {selectedContacts.size > 0 && (
        <div className="bg-lex-accent-soft border-b border-lex-accent px-6 py-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-lex-accent">
              {selectedContacts.size} contact{selectedContacts.size !== 1 ? 's' : ''} selected
            </span>
            <div className="flex space-x-2">
              <button className="px-3 py-1 text-sm bg-lex-accent text-lex-accent-contrast rounded hover:bg-lex-accent-hover">
                Export
              </button>
              <button 
                onClick={handleBulkDelete}
                disabled={isDeleting || !onDeleteContacts}
                className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-1"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 table-fixed">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedContacts.size === filteredAndSortedContacts.length && filteredAndSortedContacts.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
                />
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 w-1/4 min-w-0"
                onClick={() => handleSort('name')}
              >
                Name
                {sortField === 'name' && (
                  <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 w-20"
                onClick={() => handleSort('status')}
              >
                Status
                {sortField === 'status' && (
                  <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 w-20"
                onClick={() => handleSort('contact_type')}
              >
                Type
                {sortField === 'contact_type' && (
                  <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-1/4 min-w-0">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-1/4 min-w-0">
                Phone
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                onClick={() => handleSort('created_at')}
              >
                Created
                {sortField === 'created_at' && (
                  <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredAndSortedContacts.map((contact, idx) => (
              <tr
                key={contact.id}
                className="lex-list-row hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                style={{ '--row-index': idx } as React.CSSProperties}
                onClick={() => onContactClick(contact)}
              >
                <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedContacts.has(contact.id)}
                    onChange={() => handleSelectContact(contact.id)}
                    className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
                  />
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900 dark:text-white max-w-xs truncate" title={contact.name}>
                    {highlightText(contact.name, searchQuery)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(contact.status)}`}>
                    {contact.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {contact.contact_type}
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900 dark:text-white max-w-xs truncate" title={contact.email || '-'}>
                    {contact.email ? highlightText(contact.email, searchQuery) : '-'}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900 dark:text-white max-w-xs truncate" title={contact.phone || '-'}>
                    {contact.phone ? highlightText(contact.phone, searchQuery) : '-'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {formatDate(contact.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Empty state */}
      {filteredAndSortedContacts.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-500 dark:text-gray-400">
            <svg className="mx-auto h-12 w-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <p className="text-lg font-medium">No contacts found</p>
            <p className="mt-1">
              {searchQuery || Object.keys(filters).length > 0 
                ? 'Try adjusting your search or filters'
                : 'Get started by adding your first contact'
              }
            </p>
          </div>
        </div>
      )}

      {/* Contact Deletion Error Modal */}
      <ContactDeletionErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        errors={deletionErrors}
      />
    </div>
  );
};

export default ContactsTableView; 